import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { createUser, updateUser } from "api/graphQL_users";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { MdCheck, MdEdit, MdFileCopy, MdHighlightOff, MdInfo } from "react-icons/md";
import { MultiSelect } from "react-multi-select-component";
import { Select as AntSelect } from "antd";
import _ from "lodash";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

/* ── Step config ── */
const STEPS = [
  { key: "identity",  label: "Identity",     subtitle: "Name & role",          icon: "user" },
  { key: "contact",   label: "Contact",      subtitle: "Email & password",     icon: "contact" },
  { key: "orgs",      label: "Organizations",subtitle: "Assign organizations", icon: "org" },
];

const StepSVG = ({ icon, active, done }: { icon: string; active: boolean; done: boolean }) => {
  const col = done ? "#fff" : active ? "#fff" : "#94a3b8";
  const icons: Record<string, JSX.Element> = {
    user: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    contact: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    org: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  };
  return icons[icon] ?? null;
};

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ErrorMsg = ({ msg }: { msg?: string }) => (
  <p className={`uwz-err${msg ? "" : " uwz-err--hidden"}`} aria-live="polite">
    {msg && (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    )}
    {msg ?? ""}
  </p>
);

const CreateUserModal = ({
  fetchUsers,
  id,
  is_info,
  data,
  organizations,
  roleDataDrop,
  roleData,
  infoData,
}: {
  fetchUsers: () => void;
  id?: string;
  is_info?: boolean;
  data?: any;
  organizations?: any;
  roleDataDrop?: any;
  roleData?: any;
  infoData?: any;
}) => {
  const rootForm = {
    first_name: "",
    last_name: "",
    email: "",
    role_id: "",
    password: "",
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [initialValues, setInitialFormValues] = React.useState<any>(rootForm);
  const [viewMode, setViewMode] = React.useState<boolean>(is_info);
  const [formValuesErr, setFormValuesErr] = React.useState<any>({});
  const [selected, setSelected] = React.useState<any[]>([]);
  const [passwordShow, setPasswordShow] = React.useState(false);
  const [multiselectError, setMultiselectError] = React.useState<string>("");
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [popoverOpenE, setPopoverOpenE] = React.useState(false);

  /* wizard state */
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [animDir, setAnimDir] = useState<"forward" | "back">("forward");
  const [animating, setAnimating] = useState(false);

  const totalSteps = STEPS.length;

  const options =
    organizations?.length > 0
      ? organizations.map((d: any) => ({ label: d?.name, value: d?.id }))
      : [];

  useEffect(() => {
    if (id) setIsEdit(true);
  }, [id]);

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => {
      setIsCloseModal(false);
      onClose();
      setFormValues(rootForm);
      setFormValuesErr({});
      setMultiselectError("");
      setSelected([]);
      setCurrentStep(0);
      setCompletedSteps(new Set());
      setViewMode(is_info);
      setPopoverOpenE(false)
      setPopoverOpen(false)
    }, 320);
  };

  const emailRx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateStep = (step: number): boolean => {
    let errs: any = {};
    if (step === 0) {
      if (!formValues.first_name?.trim()) errs.first_name = "First name is required";
      if (!formValues.last_name?.trim()) errs.last_name = "Last name is required";
      if (!formValues.role_id) errs.role_id = "Role is required";
    }
    if (step === 1) {
      if (!formValues.email?.trim()) errs.email = "Email is required";
      else if (!emailRx.test(formValues.email)) errs.email = "Invalid email address";
      if (!isEdit) {
        if (!formValues.password?.trim()) errs.password = "Password is required";
        else if (formValues.password.length < 8) errs.password = "Password must be at least 8 characters";
      } else {
        if (formValues.password && formValues.password.length < 8) {
          errs.password = "Password must be at least 8 characters";
        }
      }
    }
    if (step === 2) {
      if (!selected || selected.length === 0) {
        setMultiselectError("Please select at least one organization");
        return false;
      } else {
        setMultiselectError("");
      }
    }
    setFormValuesErr((prev: any) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  };

  const animateToStep = (next: number) => {
    if (animating) return;
    const dir = next > currentStep ? "forward" : "back";
    setAnimDir(dir);
    setAnimating(true);
    setTimeout(() => { setCurrentStep(next); setAnimating(false); }, 280);
  };

  const handleNext = () => {
    if (viewMode) { animateToStep(currentStep + 1); return; }
    if (!validateStep(currentStep)) return;
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    animateToStep(currentStep + 1);
  };

  const handleBack = () => animateToStep(currentStep - 1);

  const handleStepClick = (idx: number) => {
    if (viewMode || idx < currentStep || completedSteps.has(idx)) animateToStep(idx);
  };

  const initForm = () => {
    if (data) {
      setFormValues({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        role_id: data.role_id,
        organizations: data.organizations ? data.organizations : "",
        password: "",
      });
      setInitialFormValues({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        role_id: data.role_id,
        organizations: data.organizations,
        password: "",
      });
      setSelected(
        organizations
          ?.filter((item: any) => data?.organizations?.includes(item?.id))
          ?.map((d: any) => ({ label: d?.name, value: d?.id }))
      );
    }
  };

  const hasFormChanged = () =>
    Object.keys(formValues).some(
      (key) => formValues[key] !== initialValues[key]
    );

  async function handleSubmit() {
    if (!validateStep(2)) return;
    try {
      setIsLoading(true);
      let payload: any = {
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        email: formValues.email,
        role_id: formValues.role_id,
        organizations: selected.map((d) => d?.value),
      };
      if (formValues.password) payload = { ...payload, password: formValues.password };

      if (id) {
        const hasChanges = !_.isEqual(payload, initialValues);
        if (!hasChanges) {
          toast.error("No changes were detected. You're all set!");
          handleClose();
          return;
        }
        const update = await updateUser(payload, id);
        if (update.errors) {
          toast.error(update.errors[0].message);
        } else if (update.status === "success") {
          toast.success("User updated successfully");
          fetchUsers();
          handleClose();
        } else {
          toast.error(update.data?.[0]?.message ?? "Something went wrong!");
        }
      } else {
        const create = await createUser(payload);
        if (create.errors) {
          toast.error(create.errors[0].message);
        } else {
          toast.success("User created successfully");
          fetchUsers();
          handleClose();
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Unable to save user data");
    } finally {
      setIsLoading(false);
    }
  }

  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="uwz-step-body">
            <div className="uwz-step-intro">
              <div className="uwz-step-intro-icon uwz-step-intro-icon--green">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <h3 className="uwz-step-heading">User Identity</h3>
                <p className="uwz-step-desc">Set the user's name and role.</p>
              </div>
            </div>
            <div className="uwz-fields">
              <div className="uwz-field-half uwz-field-wrap">
                <label className="uwz-label">First Name <span className="uwz-req">*</span></label>
                <input
                  type="text"
                  className={`uwz-input${formValuesErr?.first_name ? " uwz-input--err" : ""}`}
                  placeholder="John"
                  disabled={viewMode}
                  value={formValues?.first_name ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, first_name: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, first_name: "" }));
                  }}
                />
                {formValuesErr?.first_name && (
                  <ErrorMsg msg={formValuesErr?.first_name} />
                )}
              </div>
              <div className="uwz-field-half uwz-field-wrap">
                <label className="uwz-label">Last Name <span className="uwz-req">*</span></label>
                <input
                  type="text"
                  className={`uwz-input${formValuesErr?.last_name ? " uwz-input--err" : ""}`}
                  placeholder="Doe"
                  disabled={viewMode}
                  value={formValues?.last_name ?? ""}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, last_name: e.target.value }));
                    setFormValuesErr((p: any) => ({ ...p, last_name: "" }));
                  }}
                />
                {formValuesErr?.last_name && (
                  <ErrorMsg msg={formValuesErr?.last_name} />
                )}
              </div>
              <div className="uwz-field-full uwz-field-wrap">
                <label className="uwz-label">Role <span className="uwz-req">*</span></label>
                <AntSelect
                  showSearch
                  allowClear
                  placeholder="Select a role…"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.label ?? "").toUpperCase().includes(input.toUpperCase())
                  }
                  filterSort={(a, b) =>
                    String(a?.label ?? "").toLowerCase().localeCompare(String(b?.label ?? "").toLowerCase())
                  }
                  className={`uwz-ant-select${formValuesErr?.role_id ? " uwz-ant-select--err" : ""}`}
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  options={roleDataDrop?.map((m: any) => ({
                    ...m,
                    label: m?.role_name,
                    value: m?.id,
                  }))}
                  value={formValues?.role_id || undefined}
                  onChange={(e) => {
                    setFormValues((p: any) => ({ ...p, role_id: e }));
                    setFormValuesErr((p: any) => ({ ...p, role_id: "" }));
                  }}
                  notFoundContent="No roles found!"
                />
                {formValuesErr?.role_id && (
                  <ErrorMsg msg={formValuesErr?.role_id} />
                )}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="uwz-step-body">
            <div className="uwz-step-intro">
              <div className="uwz-step-intro-icon uwz-step-intro-icon--blue">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div>
                <h3 className="uwz-step-heading">Contact & Security</h3>
                <p className="uwz-step-desc">Email address and password for this user.</p>
              </div>
            </div>
            <div className="uwz-fields">
              <div className="uwz-field-full uwz-field-wrap">
                <label className="uwz-label">Email <span className="uwz-req">*</span></label>
                <div className="uwz-input-icon-wrap">
                  <span className="uwz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className={`uwz-input uwz-input--icon${formValuesErr?.email ? " uwz-input--err" : ""}`}
                    placeholder="user@company.com"
                    disabled={viewMode}
                    value={formValues?.email ?? ""}
                    onChange={(e) => {
                      setFormValues((p: any) => ({ ...p, email: e.target.value }));
                      const valid = emailRx.test(e.target.value);
                      setFormValuesErr((p: any) => ({
                        ...p,
                        email: e.target.value && !valid ? "Invalid email address" : "",
                      }));
                    }}
                  />
                </div>
                {formValuesErr?.email && (
                  <ErrorMsg msg={formValuesErr?.email} />
                )}
              </div>
              <div className="uwz-field-full uwz-field-wrap">
                <label className="uwz-label">
                  Password {!isEdit && <span className="uwz-req">*</span>}
                  {isEdit && <span className="uwz-opt"> (leave blank to keep current)</span>}
                </label>
                <div className="uwz-input-icon-wrap">
                  <span className="uwz-input-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    type={passwordShow ? "text" : "password"}
                    className={`uwz-input uwz-input--icon uwz-input--pw${formValuesErr?.password ? " uwz-input--err" : ""}`}
                    placeholder="Min. 8 characters"
                    disabled={viewMode}
                    value={formValues?.password ?? ""}
                    onChange={(e) => {
                      setFormValues((p: any) => ({ ...p, password: e.target.value }));
                      const val = e.target.value;
                      let err = "";
                      if (!isEdit && !val.trim()) err = "Password is required";
                      else if (val && val.length < 8) err = "Password must be at least 8 characters";
                      setFormValuesErr((p: any) => ({ ...p, password: err }));
                    }}
                  />
                  {!viewMode && (
                    <button
                      type="button"
                      className="uwz-pw-toggle"
                      onClick={() => setPasswordShow(!passwordShow)}
                    >
                      {passwordShow ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  )}
                </div>
                {formValuesErr?.password && (
                <ErrorMsg msg={formValuesErr?.password} />
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="uwz-step-body">
            <div className="uwz-step-intro">
              <div className="uwz-step-intro-icon uwz-step-intro-icon--purple">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <div>
                <h3 className="uwz-step-heading">Organizations</h3>
                <p className="uwz-step-desc">Assign this user to one or more organizations.</p>
              </div>
            </div>
            <div className="uwz-fields">
              <div className="uwz-field-full uwz-field-wrap">
                <label className="uwz-label">Organizations <span className="uwz-req">*</span></label>
                <div className={`uwz-multiselect-wrap${multiselectError ? " uwz-multiselect-wrap--err" : ""}`}>
                  <MultiSelect
                    options={options}
                    value={selected}
                    onChange={(sel: any) => {
                      setSelected(sel);
                      setFormValues((prev: any) => ({
                        ...prev,
                        organizations: sel.map((o: any) => o.value),
                      }));
                      if (sel?.length > 0) setMultiselectError("");
                      else setMultiselectError("Please select at least one organization");
                    }}
                    labelledBy="Select"
                    disabled={viewMode}
                    overrideStrings={{ noOptions: "No organizations found!" }}
                    valueRenderer={(sel, opts) => {
                      if (sel.length === 0) return "Select organization(s)…";
                      if (sel.length === opts.length) return "All organizations selected";
                      return sel.map((o) => o.label).join(", ");
                    }}
                  />
                </div>
                {multiselectError && (
                  <ErrorMsg msg={multiselectError} />
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === totalSteps - 1;
  const progressPct = (currentStep / (totalSteps - 1)) * 100;

  /* ── Trigger element ── */
  const TriggerEl = () => {
    if (is_info) {
      if (!roleData?.[0]?.user?.value?.view_user) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpen && !isOpen} onOpen={() => setPopoverOpen(true)} onClose={() => setPopoverOpen(false)}>
          <PopoverTrigger>
            <button
              onClick={() => {
                if (data) initForm();
                setViewMode(true);
                setCurrentStep(0);
                onOpen();
              }}
              onMouseEnter={() => !isOpen && setPopoverOpen(true)} onMouseLeave={() => setPopoverOpen(false)}
            >
              <div className="usr-action-icon usr-action-icon--blue">
                <MdInfo size={16} />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">
              View User
            </PopoverHeader>
            <PopoverArrow bg="#eff6ff" />
          </PopoverContent>
        </Popover>
      );
    }
    if (id) {
      if (!roleData?.[0]?.user?.value?.edit_user) return null;
      return (
        <Popover trigger="hover" placement="bottom" closeOnBlur  closeOnEsc isOpen={popoverOpenE && !isOpen} onOpen={() => setPopoverOpenE(true)} onClose={() => setPopoverOpenE(false)}>
          <PopoverTrigger>
            <button
              onClick={() => {
                if (data) initForm();
                setViewMode(false);
                setCurrentStep(0);
                onOpen();
              }}
              onMouseEnter={() => !isOpen && setPopoverOpenE(true)} onMouseLeave={() => setPopoverOpenE(false)}
            >
              <div className="usr-action-icon usr-action-icon--amber">
                <MdEdit size={16} />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent border="1px solid #fde68a" borderRadius="12px" bg="#fffbeb" maxW="200px" p="0" overflow="hidden" zIndex="20">
            <PopoverHeader bg="#fef3c7" fontSize="12px" fontWeight="700" color="#92400e" borderBottom="1px solid #fde68a" px="12px" py="9px">
              Edit User
            </PopoverHeader>
            <PopoverArrow bg="#fffbeb" />
          </PopoverContent>
        </Popover>
      );
    }
    if (!roleData?.[0]?.user?.value?.add_user) return null;
    return (
      <button
        className="usr-add-btn"
        onClick={() => {
          setViewMode(false);
          setCurrentStep(0);
          setCompletedSteps(new Set());
          onOpen();
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="16"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
        </svg>
        Add New User
      </button>
    );
  };

  return (
    <>
      <TriggerEl />

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`uwz-panel${isOpen && !isCloseModal ? " uwz-panel--open" : " uwz-panel--close"}`}>

          {/* Decorative top gradient bar */}
          <div className="uwz-top-glow" />

          {/* Header */}
          <div className="uwz-header">
            <div className="uwz-header-brand">
              <div className="uwz-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {viewMode ? (
                    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#fff"/></>
                  ) : id ? (
                    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                  ) : (
                    <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>
                  )}
                </svg>
              </div>
              <div>
                <h2 className="uwz-header-title">
                  {viewMode
                    ? "User Details"
                    : id
                    ? `Edit — ${infoData?.row?.original?.first_name ?? ""} ${infoData?.row?.original?.last_name ?? ""}`
                    : "New User"}
                </h2>
                <p className="uwz-header-sub">
                  Step {currentStep + 1} of {totalSteps} · {STEPS[currentStep].label}
                </p>
              </div>
            </div>
            <div className="uwz-header-right">
              {viewMode && roleData?.[0]?.user?.value?.edit_user && (
                <button className="uwz-edit-btn" onClick={() => setViewMode(false)}>
                  <MdEdit size={13} /> Switch to Edit
                </button>
              )}
              <button className="uwz-close-btn" onClick={handleClose} aria-label="Close">
                <MdHighlightOff size={22} />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="uwz-progress-track">
            <div className="uwz-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          {/* Step indicators */}
          <div className="uwz-steps-row">
            {STEPS.map((step, i) => {
              const isActive = i === currentStep;
              const isDone = completedSteps.has(i);
              const isReach = viewMode || i <= currentStep || completedSteps.has(i);
              return (
                <div key={step.key} className="uwz-step-item" onClick={() => isReach && handleStepClick(i)}>
                  <div className={`uwz-step-circle${isActive ? " uwz-step-circle--active" : isDone ? " uwz-step-circle--done" : " uwz-step-circle--idle"}`}>
                    {isDone && !isActive ? <CheckIcon /> : <StepSVG icon={step.icon} active={isActive} done={isDone} />}
                    {isActive && <div className="uwz-step-pulse" />}
                  </div>
                  <div className="uwz-step-label-block">
                    <span className={`uwz-step-label${isActive ? " uwz-step-label--active" : isDone ? " uwz-step-label--done" : ""}`}>
                      {step.label}
                    </span>
                    <span className="uwz-step-sublabel">{step.subtitle}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`uwz-step-connector${isDone ? " uwz-step-connector--done" : ""}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="uwz-content-area">
            <div className={`uwz-anim-wrap${animating ? ` uwz-anim--exit-${animDir}` : ` uwz-anim--enter-${animDir}`}`}>
              {renderStep(currentStep)}
            </div>
          </div>

          {/* Footer nav */}
          <div className="uwz-footer">
            <div className="uwz-footer-left">
              <div className="uwz-dot-row">
                {STEPS.map((_, i) => (
                  <div key={i} className={`uwz-dot${i === currentStep ? " uwz-dot--active" : completedSteps.has(i) ? " uwz-dot--done" : ""}`} />
                ))}
              </div>
              <span className="uwz-footer-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Encrypted &amp; secure
              </span>
            </div>
            <div className="uwz-footer-btns">
              {currentStep > 0 && (
                <button className="uwz-btn-back" onClick={handleBack}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  Back
                </button>
              )}
              <button className="uwz-btn-cancel" onClick={handleClose}>Cancel</button>

              {isLastStep ? (
                !viewMode && (
                  <button
                    className="uwz-btn-submit"
                    onClick={handleSubmit}
                    disabled={isLoading || (!!id && !hasFormChanged())}
                  >
                    {isLoading ? (
                      <span className="uwz-spinner" />
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {id ? "Update User" : "Create User"}
                      </>
                    )}
                  </button>
                )
              ) : (
                <button className="uwz-btn-next" onClick={handleNext}>
                  {viewMode ? "Next" : "Continue"}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Add button ── */
        .usr-add-btn {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #15803d, #16a34a);
          cursor: pointer; box-shadow: 0 3px 12px rgba(22,163,74,.3);
          transition: transform .15s, box-shadow .18s;
        }
        .usr-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(22,163,74,.38); }

        /* ── Action icons ── */
        .usr-action-icon {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all .15s; border: 1.5px solid;
        }
        .usr-action-icon--blue { background: #eff6ff; border-color: #bfdbfe; color: #2563eb; }
        .usr-action-icon--blue:hover { background: #dbeafe; border-color: #93c5fd; }
        .usr-action-icon--amber { background: #fffbeb; border-color: #fde68a; color: #d97706; }
        .usr-action-icon--amber:hover { background: #fef3c7; border-color: #fcd34d; }

        /* ── Slide panel ── */
        .uwz-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 780px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .uwz-panel--open { transform: translateX(0); }
        .uwz-panel--close { transform: translateX(100%); }

        /* Top glow */
        .uwz-top-glow {
          height: 3px;
          background: linear-gradient(90deg,#14532d,#16a34a,#7c3aed,#16a34a,#14532d);
          background-size: 200% 100%;
          animation: uwzGlow 3s linear infinite; flex-shrink: 0;
        }
        @keyframes uwzGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .uwz-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#14532d 0%,#166534 60%,#15803d 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .uwz-header-brand { display: flex; align-items: center; gap: 13px; }
        .uwz-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .uwz-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
        .uwz-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .uwz-header-right { display: flex; align-items: center; gap: 10px; }
        .uwz-edit-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 700; color: #fff;
          background: rgba(255,255,255,.14); border: 1.5px solid rgba(255,255,255,.25);
          border-radius: 8px; padding: 7px 12px; cursor: pointer; transition: background .15s;
        }
        .uwz-edit-btn:hover { background: rgba(255,255,255,.24); }
        .uwz-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65); display: flex; align-items: center; padding: 2px; transition: color .15s;
        }
        .uwz-close-btn:hover { color: #fff; }

        /* Progress bar */
        .uwz-progress-track { height: 4px; background: #e8edf3; flex-shrink: 0; }
        .uwz-progress-fill { height: 100%; background: linear-gradient(90deg,#15803d,#7c3aed); border-radius: 0 4px 4px 0; transition: width .4s cubic-bezier(.4,0,.2,1); }

        /* Steps row */
        .uwz-steps-row {
          display: flex; align-items: flex-start;
          padding: 16px 22px 12px;
          background: #fafbfc; border-bottom: 1px solid #f1f5f9;
          flex-shrink: 0; overflow-x: auto; gap: 0;
        }
        .uwz-step-item { display: flex; align-items: center; flex: 1; min-width: 0; cursor: default; }

        .uwz-step-circle {
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; position: relative; transition: all .25s;
        }
        .uwz-step-circle--idle   { background: #f1f5f9; border: 2px solid #e2e8f0; }
        .uwz-step-circle--active { background: linear-gradient(135deg,#15803d,#16a34a); border: 2px solid transparent; box-shadow: 0 0 0 4px rgba(22,163,74,.15); }
        .uwz-step-circle--done   { background: linear-gradient(135deg,#15803d,#16a34a); border: 2px solid transparent; box-shadow: 0 0 0 3px rgba(22,163,74,.12); }

        .uwz-step-pulse { position: absolute; inset: -4px; border-radius: 50%; border: 2px solid rgba(22,163,74,.35); animation: uwzPulse 1.8s ease-in-out infinite; }
        @keyframes uwzPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0;transform:scale(1.4)} }

        .uwz-step-label-block { display: flex; flex-direction: column; gap: 1px; padding: 0 6px; min-width: 0; }
        .uwz-step-label { font-size: 11.5px; font-weight: 600; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .uwz-step-label--active { color: #15803d; font-weight: 800; }
        .uwz-step-label--done { color: #15803d; font-weight: 700; }
        .uwz-step-sublabel { font-size: 10px; color: #cbd5e1; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .uwz-step-connector { flex: 1; height: 2px; background: #e2e8f0; margin: 0 6px; border-radius: 2px; transition: background .3s; }
        .uwz-step-connector--done { background: linear-gradient(90deg,#16a34a,#22c55e); }

        /* Content area */
        .uwz-content-area { flex: 1; overflow-y: auto; overflow-x: hidden; position: relative; }
        .uwz-content-area::-webkit-scrollbar { width: 4px; }
        .uwz-content-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Animations */
        .uwz-anim-wrap { transition: opacity .25s ease, transform .28s cubic-bezier(0.16,1,0.3,1); }
        .uwz-anim--enter-forward { animation: uwzSlideInRight .28s cubic-bezier(0.16,1,0.3,1) both; }
        .uwz-anim--enter-back    { animation: uwzSlideInLeft  .28s cubic-bezier(0.16,1,0.3,1) both; }
        .uwz-anim--exit-forward  { animation: uwzSlideOutLeft .18s ease both; }
        .uwz-anim--exit-back     { animation: uwzSlideOutRight .18s ease both; }
        @keyframes uwzSlideInRight  { from{opacity:0;transform:translateX(32px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes uwzSlideInLeft   { from{opacity:0;transform:translateX(-32px)} to{opacity:1;transform:translateX(0)} }
        @keyframes uwzSlideOutLeft  { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(-24px)} }
        @keyframes uwzSlideOutRight { from{opacity:1;transform:translateX(0)} to{opacity:0;transform:translateX(24px)} }

        /* Step body */
        .uwz-step-body { padding: 24px 28px 8px; }
        .uwz-step-intro { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
        .uwz-step-intro-icon { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .uwz-step-intro-icon--green  { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .uwz-step-intro-icon--blue   { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .uwz-step-intro-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .uwz-step-heading { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -.3px; margin-bottom: 3px; line-height: 22px; }
        .uwz-step-desc { font-size: 13px; color: #64748b; font-weight: 500; line-height: 1.5; }

        /* Form fields */
        .uwz-fields { display: grid; grid-template-columns: 1fr 1fr; column-gap: 16px; row-gap: 8px; align-items: start; }
        .uwz-field-full  { grid-column: 1/-1; }
        .uwz-field-half  { grid-column: span 1; }
        .uwz-field-wrap  { display: flex; flex-direction: column; }

        .uwz-label { font-size: 12.5px; font-weight: 700; color: #374151; margin-bottom: 6px; display: block; }
        .uwz-req   { color: #dc2626; }
        .uwz-opt   { font-weight: 500; color: #94a3b8; font-size: 11.5px; }

        .uwz-input {
          width: 100%; padding: 12px 14px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s, background .18s;
          box-sizing: border-box;
        }
        .uwz-input::placeholder { color: #cbd5e1; }
        .uwz-input:focus { border-color: #16a34a; background: #fff; box-shadow: 0 0 0 3px rgba(22,163,74,.1); }
        .uwz-input--err { border-color: #ef4444 !important; background: #fff7f7 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.08) !important; }
        .uwz-input:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
        .uwz-input--icon { padding-left: 40px; }
        .uwz-input--pw { padding-right: 44px; }

        .uwz-input-icon-wrap { position: relative; }
        .uwz-input-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; display: flex; align-items: center; pointer-events: none; }
        .uwz-pw-toggle {
          position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer; color: #94a3b8;
          display: flex; align-items: center; padding: 0; transition: color .15s;
        }
        .uwz-pw-toggle:hover { color: #374151; }

        /* Ant Select */
        .uwz-ant-select {display:table !important;}
        .uwz-ant-select .ant-select-selector { height: 46px !important; border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important; background: #f8fafc !important; font-family: 'Plus Jakarta Sans', sans-serif !important; }
        .uwz-ant-select .ant-select-selection-search-input { height: 44px !important; }
        .uwz-ant-select .ant-select-selection-placeholder { line-height: 44px !important; color: #cbd5e1 !important; }
        .uwz-ant-select .ant-select-selection-item { line-height: 44px !important; }
        .uwz-ant-select--err .ant-select-selector { border-color: #ef4444 !important; background: #fff7f7 !important; }

        /* Multiselect — table-layout trick prevents width growth on multi-select */
        .uwz-multiselect-wrap {
          display: table !important; table-layout: fixed !important;
          width: 100% !important; min-width: 0 !important;
          position: relative; border-radius: 11px; overflow: visible;
        }
        .uwz-multiselect-wrap .dropdown-container {
          display: block !important; width: 100% !important; max-width: 100% !important;
          min-width: 0 !important; box-sizing: border-box !important;
          border: 1.5px solid #e2e8f0 !important; border-radius: 11px !important;
          background: #f8fafc !important; font-family: 'Plus Jakarta Sans', sans-serif !important;
          overflow: visible !important;
        }
        .uwz-multiselect-wrap .dropdown-container:focus-within {
          border-color: #16a34a !important; box-shadow: 0 0 0 3px rgba(22,163,74,.1) !important;
          background: #fff !important;
        }
        .uwz-multiselect-wrap--err .dropdown-container {
          border-color: #ef4444 !important; background: #fff7f7 !important;
        }
        .uwz-multiselect-wrap .dropdown-heading {
          display: flex !important; align-items: center !important;
          height: 46px !important; min-height: 46px !important; max-height: 46px !important;
          padding: 0 12px !important; overflow: hidden !important;
          width: 100% !important; max-width: 100% !important; box-sizing: border-box !important;
        }
        .uwz-multiselect-wrap .dropdown-heading-value {
          flex: 1 1 0% !important; min-width: 0 !important;
          max-width: 100% !important; overflow: hidden !important;
        }
        .uwz-multiselect-wrap .dropdown-heading-value span,
        .uwz-multiselect-wrap .dropdown-heading-value > * {
          display: block !important; overflow: hidden !important;
          text-overflow: ellipsis !important; white-space: nowrap !important;
          width: 100% !important; font-size: 13.5px !important; color: #0f172a !important;
        }
        .uwz-multiselect-wrap .dropdown-content {
          z-index: 9999 !important; border: 1.5px solid #e2e8f0 !important;
          border-radius: 10px !important; box-shadow: 0 8px 24px rgba(15,23,42,0.12) !important;
          overflow: visible !important; position: absolute !important; width: 100% !important;
        }
        .uwz-multiselect-wrap .options {
          max-height: 220px !important; overflow-y: auto !important;
          border-radius: 0 0 10px 10px !important;
        }
        .uwz-multiselect-wrap .options li label { font-size: 13px !important; color: #374151 !important; }
        .uwz-multiselect-wrap input[type="text"] { font-size: 13px !important; }
        .dropdown-content { z-index: 9999 !important; }
        @media (max-width: 480px) {
          .uwz-multiselect-wrap .dropdown-heading {
            height: 42px !important; min-height: 42px !important; max-height: 42px !important;
          }
          .uwz-multiselect-wrap .dropdown-heading-value span,
          .uwz-multiselect-wrap .dropdown-heading-value > * { font-size: 12.5px !important; }
        }

        /* Error */
        .uwz-err { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #dc2626; font-weight: 500; margin-top: 5px; min-height: 21px; line-height: 1.4; }
        .uwz-err--hidden { visibility: hidden; }

        /* Footer */
        .uwz-footer {
          padding: 14px 26px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .uwz-footer-left { display: flex; align-items: center; gap: 16px; }
        .uwz-dot-row { display: flex; gap: 6px; align-items: center; }
        .uwz-dot { width: 7px; height: 7px; border-radius: 50%; background: #e2e8f0; transition: all .2s; }
        .uwz-dot--active { width: 20px; border-radius: 4px; background: linear-gradient(90deg,#15803d,#16a34a); }
        .uwz-dot--done { background: #16a34a; }
        .uwz-footer-hint { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .uwz-footer-btns { display: flex; align-items: center; gap: 8px; }

        .uwz-btn-back {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .uwz-btn-back:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        .uwz-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .uwz-btn-cancel:hover { background: #f1f5f9; color: #374151; }

        .uwz-btn-next {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#15803d,#16a34a);
          cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,.3);
          transition: transform .15s, box-shadow .18s; position: relative; overflow: hidden;
        }
        .uwz-btn-next::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .uwz-btn-next:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(22,163,74,.4); }
        .uwz-btn-next:disabled { opacity: .6; cursor: not-allowed; transform: none; }

        .uwz-btn-submit {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#15803d,#16a34a);
          cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,.3);
          transition: transform .15s, box-shadow .18s, opacity .18s; position: relative; overflow: hidden;
        }
        .uwz-btn-submit::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(255,255,255,.12),transparent 60%); }
        .uwz-btn-submit:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(22,163,74,.4); }
        .uwz-btn-submit:disabled { opacity: .5; cursor: not-allowed; transform: none; }

        .uwz-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: uwzSpin .65s linear infinite; }
        @keyframes uwzSpin { to{transform:rotate(360deg)} }

        @media (max-width: 640px) {
          .uwz-panel { max-width: 100%; }
          .uwz-header { padding: 16px 18px; }
          .uwz-steps-row { padding: 12px 14px 10px; }
          .uwz-step-label-block { display: none; }
          .uwz-step-body { padding: 20px 18px 8px; }
          .uwz-footer { padding: 12px 18px; }
          .uwz-fields { grid-template-columns: 1fr; }
          .uwz-field-half { grid-column: span 1; }
        }
      `}</style>
    </>
  );
};

export default CreateUserModal;

/* ── TruncateCopy helper ── */
export const TruncateCopy = ({
  is_info,
  slice = 15,
  showCopy = true,
}: {
  is_info: any;
  slice?: number;
  showCopy?: boolean;
}) => {
  const [copy, setCopy] = React.useState<boolean>(false);
  return (
    <div className="flex items-center gap-2">
      <p className="overflow-auto text-sm font-bold text-navy-700 dark:text-white">
        {is_info?.getValue()}
      </p>
      {showCopy && (
        <>
          {copy ? (
            <MdCheck className="cursor-pointer text-teal-500" />
          ) : (
            <MdFileCopy
              className="cursor-pointer text-teal-500"
              onClick={() => {
                navigator.clipboard.writeText(is_info?.getValue());
                setCopy(true);
                setTimeout(() => setCopy(false), 1500);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};